/* iter-tablet-tip · Oct-2026.
 *
 * One-time (not per-session — persists across sessions via
 * localStorage) onboarding tip shown on a tablet's first visit to any
 * "/loom", "/loom/editor", "/app", or "/app/business" surface. Points
 * out pinch-to-zoom and the mini-map, the two controls a first-time
 * tablet visitor is least likely to discover on their own.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Hand, Map as MapIcon, X } from 'lucide-react';

const STORAGE_KEY = 'pw:loom-tablet-tip-seen';

export const TabletLoomTip = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) !== '1'; } catch (_e) { return true; }
  });

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (_e) { /* noop */ }
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <aside
      className="absolute bottom-3 left-3 z-30 flex max-w-xs items-start gap-2.5 rounded-md border border-border bg-card/95 p-3 shadow-lg backdrop-blur-sm"
      data-testid="loom-tablet-tip"
    >
      <div className="flex shrink-0 flex-col gap-1.5 pt-0.5">
        <Hand className="h-4 w-4 text-primary" />
        <MapIcon className="h-4 w-4 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11.5px] font-semibold" data-testid="loom-tablet-tip-title">{t('loomAdvisory.tabletTitle')}</div>
        <p className="mt-0.5 text-[11px] leading-5 text-muted-foreground" data-testid="loom-tablet-tip-body">
          {t('loomAdvisory.tabletBody')}
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('loomAdvisory.tabletDismissAria')}
        data-testid="loom-tablet-tip-dismiss"
        className="grid h-6 w-6 shrink-0 place-items-center rounded-sm text-muted-foreground hover:bg-secondary"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </aside>
  );
};
